import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError, getAccessToken } from "@/api/client";
import { useStore } from "@/store/StoreContext";
import { Badge, Button, Drawer, Field, Input, Select } from "@/components/ui";
import { DateInput } from "@/components/DateInput";
import { DOW, addDays, fmtIsoDate, mondayOf, todayIso } from "@/lib/dates";

type Row = { weekday: number; start_time: string; end_time: string; parity?: number };
type Override = { id: string; date: string; start_time: string | null; end_time: string | null; off: boolean };
type Leave = { id: string; starts_on: string; ends_on: string; kind: string; reason: string | null };
type WeekStaff = { staff: { id: string; display_name: string; color: string; active: boolean }; source: "week" | "template"; parity: number; rows: Row[]; template: Row[]; overrides: Override[]; time_off: Leave[] };
type WeekOut = { week: string; biweekly_anchor: string | null; parity: number; staff: WeekStaff[] };
type DayRow = { on: boolean; start: string; end: string };

const hm = (s: string) => s.slice(0, 5);
const toRows = (rows: Row[]): DayRow[] => Array.from({ length: 7 }, (_, d) => { const r = rows.find((x) => x.weekday === d); return r ? { on: true, start: hm(r.start_time), end: hm(r.end_time) } : { on: false, start: "09:00", end: "17:00" }; });
const fromRows = (rows: DayRow[], parity?: number): Row[] => rows.flatMap((r, d) => (r.on ? [{ weekday: d, start_time: `${r.start}:00`, end_time: `${r.end}:00`, ...(parity !== undefined ? { parity } : {}) }] : []));

function DayRows({ rows, onChange, lang }: { rows: DayRow[]; onChange: (r: DayRow[]) => void; lang: string }) {
  const set = (d: number, patch: Partial<DayRow>) => onChange(rows.map((r, i) => (i === d ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-1">{rows.map((r, d) => (
      <div key={d} className="grid grid-cols-[40px_auto_1fr_1fr] items-center gap-2 text-sm">
        <span>{DOW[lang === "el" ? "el" : "en"][d]}</span><input type="checkbox" checked={r.on} onChange={(e) => set(d, { on: e.target.checked })} />
        <Input type="time" value={r.start} disabled={!r.on} onChange={(e) => set(d, { start: e.target.value })} />
        <Input type="time" value={r.end} disabled={!r.on} onChange={(e) => set(d, { end: e.target.value })} />
      </div>))}</div>
  );
}

/** Week-by-week shift planner: the effective hours per technician, this-week-only edits (history is never rewritten),
 * the recurring template (single or A/B bi-weekly), copy-previous, and an Excel export of any date range. */
export function ShiftsPage() {
  const { store } = useStore();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const [week, setWeek] = useState(mondayOf(todayIso()));
  const [only, setOnly] = useState(sp.get("staff") ?? "");
  const [editing, setEditing] = useState<{ kind: "week" | "template"; s: WeekStaff } | null>(null);
  const [exp, setExp] = useState<{ from: string; to: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const q = useQuery({ queryKey: ["shifts", store?.id, week], queryFn: () => api<WeekOut>(`/app/shifts?store_id=${store!.id}&week=${week}`), enabled: !!store });
  const invalidate = () => { for (const k of ["shifts", "staff", "calendar", "orphans", "availability"]) qc.invalidateQueries({ queryKey: [k] }); };
  const act = useMutation({
    mutationFn: ({ path, method, body }: { path: string; method: string; body?: unknown }) => api(path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
    onSuccess: () => { setError(null); setEditing(null); invalidate(); }, onError: (e) => setError(e instanceof ApiError ? e.detail : t("app.error")),
  });
  if (!store) return null;
  const data = q.data;
  const anchor = data?.biweekly_anchor ?? null;
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const lang = i18n.language === "el" ? "el" : "en";
  const cell = (s: WeekStaff, d: number) => {
    const iso = days[d];
    const off = s.time_off.find((l) => l.starts_on <= iso && l.ends_on >= iso);
    if (off) return <Badge tone="amber">{t(`leave.kind.${off.kind}`)}</Badge>;
    const ov = s.overrides.find((o) => o.date === iso);
    if (ov) return ov.off ? <span className="text-amber-700">{t("shifts.off")} *</span> : <span className="text-amber-700">{hm(ov.start_time!)}–{hm(ov.end_time!)} *</span>;
    const r = s.rows.find((x) => x.weekday === d);
    return r ? <span>{hm(r.start_time)}–{hm(r.end_time)}</span> : <span className="text-gray-300">—</span>;
  };
  const download = async () => {
    if (!exp) return;
    const r = await fetch(`/api/v1/app/shifts/export?store_id=${store.id}&date_from=${exp.from}&date_to=${exp.to}${only ? `&staff_id=${only}` : ""}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
    if (!r.ok) { setError(t("app.error")); return; }
    const blob = await r.blob(); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `shifts_${exp.from}_${exp.to}.xlsx`; a.click();
  };
  const staffList = (data?.staff ?? []).filter((s) => !only || s.staff.id === only);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="mr-2 text-2xl font-semibold">{t("nav.shifts")}</h1>
        <Button variant="ghost" onClick={() => setWeek(addDays(week, -7))}>‹</Button>
        <Button variant="ghost" onClick={() => setWeek(mondayOf(todayIso()))}>{t("calendar.today")}</Button>
        <Button variant="ghost" onClick={() => setWeek(addDays(week, 7))}>›</Button>
        <DateInput className="!w-40" value={week} onChange={(e) => e.target.value && setWeek(mondayOf(e.target.value))} />
        <span className="text-sm text-gray-600">{fmtIsoDate(week)} – {fmtIsoDate(addDays(week, 6))}</span>
        {anchor && data && <Badge tone="blue">{t("shifts.week")} {data.parity === 1 ? "A" : "B"}</Badge>}
        <div className="ml-auto flex items-center gap-2">
          <Select className="!w-auto" value={only} onChange={(e) => setOnly(e.target.value)}><option value="">{t("shifts.everyone")}</option>{data?.staff.map((s) => <option key={s.staff.id} value={s.staff.id}>{s.staff.display_name}</option>)}</Select>
          <Button variant="ghost" onClick={() => setExp(exp ? null : { from: week, to: addDays(week, 27) })}>{t("shifts.export")}</Button>
        </div>
      </div>
      <p className="text-xs text-gray-500">{anchor ? t("shifts.anchorHint", { date: fmtIsoDate(anchor) }) : t("shifts.noAnchorHint")} {t("shifts.legend")}</p>
      {exp && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border bg-white p-3 text-sm">
          <Field label={t("shifts.from")}><DateInput className="!w-40" value={exp.from} onChange={(e) => setExp({ ...exp, from: e.target.value })} /></Field>
          <Field label={t("shifts.to")}><DateInput className="!w-40" value={exp.to} min={exp.from} onChange={(e) => setExp({ ...exp, to: e.target.value })} /></Field>
          <Button disabled={!exp.from || !exp.to} onClick={download}>{t("shifts.downloadXlsx")}</Button>
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="overflow-auto rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr><th className="p-2">{t("staff.name")}</th>{days.map((d, i) => <th key={d} className={`p-2 whitespace-nowrap ${d === todayIso() ? "text-brand" : ""}`}>{DOW[lang][i]} {fmtIsoDate(d).slice(0, 5)}</th>)}<th className="p-2"></th></tr>
          </thead>
          <tbody>
            {staffList.map((s) => (
              <tr key={s.staff.id} className={`cursor-pointer border-t hover:bg-gray-50 ${!s.staff.active ? "opacity-60" : ""}`} onClick={() => setEditing({ kind: "week", s })}>
                <td className="p-2 whitespace-nowrap">
                  <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: s.staff.color }} /><span className="font-medium">{s.staff.display_name}</span>
                  <div className="text-[11px] text-gray-500">{s.source === "week" ? t("shifts.thisWeekOnly") : `${t("shifts.template")}${anchor ? ` ${s.parity === 1 ? "A" : "B"}` : ""}`}</div>
                </td>
                {days.map((_, i) => <td key={i} className="p-2 whitespace-nowrap">{cell(s, i)}</td>)}
                <td className="p-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-flex gap-1">
                    <Button type="button" variant="ghost" className="!px-2 !py-0.5 text-xs" title={t("shifts.editWeekHint")} onClick={() => setEditing({ kind: "week", s })}>✎ {t("shifts.editWeek")}</Button>
                    <Button type="button" variant="ghost" className="!px-2 !py-0.5 text-xs" title={t("shifts.templateHint")} onClick={() => setEditing({ kind: "template", s })}>🔁 {t("shifts.editTemplate")}</Button>
                    <Button type="button" variant="ghost" className="!px-2 !py-0.5 text-xs" title={t("shifts.copyHint")} disabled={act.isPending} onClick={() => act.mutate({ path: `/app/staff/${s.staff.id}/shifts/week/${week}/copy-previous`, method: "POST" })}>📋 {t("shifts.copyPrevious")}</Button>
                    {s.source === "week" && <Button type="button" variant="ghost" className="!px-2 !py-0.5 text-xs text-amber-700" title={t("shifts.resetHint")} disabled={act.isPending} onClick={() => act.mutate({ path: `/app/staff/${s.staff.id}/shifts/week/${week}`, method: "DELETE" })}>↺ {t("shifts.resetTemplate")}</Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing ? `${editing.s.staff.display_name} · ${editing.kind === "week" ? t("shifts.editWeek") : t("shifts.editTemplate")}` : ""}>
        {editing?.kind === "week" && <WeekForm s={editing.s} week={week} lang={lang} pending={act.isPending} onSave={(rows) => act.mutate({ path: `/app/staff/${editing.s.staff.id}/shifts/week/${week}`, method: "PUT", body: rows })} />}
        {editing?.kind === "template" && <TemplateForm s={editing.s} anchor={anchor} lang={lang} pending={act.isPending} onSave={(rows) => act.mutate({ path: `/app/staff/${editing.s.staff.id}/shifts`, method: "PUT", body: rows })} />}
      </Drawer>
    </div>
  );
}

function WeekForm({ s, week, lang, pending, onSave }: { s: WeekStaff; week: string; lang: string; pending: boolean; onSave: (rows: Row[]) => void }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(toRows(s.rows));
  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs text-gray-500">{t("shifts.weekHint", { from: fmtIsoDate(week), to: fmtIsoDate(addDays(week, 6)) })}</p>
      <DayRows rows={rows} onChange={setRows} lang={lang} />
      <Button disabled={pending} onClick={() => onSave(fromRows(rows))}>{t("app.save")}</Button>
    </div>
  );
}

function TemplateForm({ s, anchor, lang, pending, onSave }: { s: WeekStaff; anchor: string | null; lang: string; pending: boolean; onSave: (rows: Row[]) => void }) {
  const { t } = useTranslation();
  const base = s.template.filter((r) => !r.parity);
  const a0 = s.template.filter((r) => r.parity === 1);
  const b0 = s.template.filter((r) => r.parity === 2);
  const [biweekly, setBiweekly] = useState(!!anchor && (a0.length > 0 || b0.length > 0));
  const [rowsA, setRowsA] = useState(toRows(a0.length ? a0 : base));
  const [rowsB, setRowsB] = useState(toRows(b0.length ? b0 : a0.length ? a0 : base));
  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs text-gray-500">{t("shifts.templateHint")}</p>
      {anchor && <label className="flex items-center gap-2"><input type="checkbox" checked={biweekly} onChange={(e) => setBiweekly(e.target.checked)} /> {t("shifts.biweekly")}</label>}
      {biweekly ? (
        <>
          <div className="font-medium">{t("shifts.week")} A</div><DayRows rows={rowsA} onChange={setRowsA} lang={lang} />
          <div className="flex items-center gap-2 font-medium">{t("shifts.week")} B <button type="button" className="text-xs font-normal underline" onClick={() => setRowsB(rowsA.map((r) => ({ ...r })))}>{t("shifts.sameAsA")}</button></div><DayRows rows={rowsB} onChange={setRowsB} lang={lang} />
        </>
      ) : <DayRows rows={rowsA} onChange={setRowsA} lang={lang} />}
      <Button disabled={pending} onClick={() => onSave(biweekly ? [...fromRows(rowsA, 1), ...fromRows(rowsB, 2)] : fromRows(rowsA, 0))}>{t("app.save")}</Button>
    </div>
  );
}
